import { Link } from "react-router-dom";
import GuidePage from "@/components/marketing/GuidePage";
import { BRAND, LAB, LAB_REPORT, PURITY_REPORT } from "@/content/brand";
import { AYURVEDA_HERITAGE } from "@/content/labExplained";
import { Table } from "@/pages/guides/BuyingGuides";

/**
 * Perspectives: guides that set coconut oil against a wider frame.
 *  - AyurvedaCoconutOil: what classical texts say, as heritage only.
 *  - CoconutOilVsOtherOils: an honest comparison with other kitchen oils.
 *
 * Source notes: docs/content/OILS_COMPARISON_RESEARCH.md and
 * docs/content/LAB_PARAMETERS_RESEARCH.md (Parts A, D and E).
 * Compliance: no disease, aphrodisiac or function claims, no verse numbers,
 * no "Ayurvedic oil", no smoke points, no knocking named brands.
 */

const UPDATED = "2026-09-25";

const labRow = (param) => LAB_REPORT.rows.find((r) => r.param === param);
const sat = labRow("Saturated fat");
const mufa = labRow("Monounsaturated fat");
const pufa = labRow("Polyunsaturated fat");
const iodine = PURITY_REPORT.rows.find((r) => r.param.startsWith("Iodine"));

const SRC = {
  jaims: AYURVEDA_HERITAGE.sources[0],
  jdras: AYURVEDA_HERITAGE.sources[1],
  ijapr: AYURVEDA_HERITAGE.sources[2],
  circulation: {
    label: "Neelakantan, Seah and van Dam, The effect of coconut oil consumption on cardiovascular risk factors, Circulation 2020",
    url: "https://www.ahajournals.org/doi/10.1161/CIRCULATIONAHA.119.043052",
  },
  eyres: {
    label: "Eyres et al., Coconut oil consumption and cardiovascular risk factors in humans, Nutrition Reviews 2016",
    url: "https://pubmed.ncbi.nlm.nih.gov/26946252/",
  },
  hair: {
    label: "Rele and Mohile, Effect of mineral oil, sunflower oil and coconut oil on prevention of hair damage, J Cosmet Sci 2003",
    url: "https://www.researchgate.net/publication/10787534_Effect_of_mineral_oil_sunflower_oil_and_coconut_oil_on_prevention_of_hair_damage",
  },
  ninFaq: { label: "ICMR NIN FAQs on using a combination of oils", url: "https://www.nin.res.in/faqs.html" },
  ninDgi: {
    label: "ICMR NIN, Dietary Guidelines for Indians, 2024",
    url: "https://www.nin.res.in/dietaryguidelines/pdfjs/locale/DGI_2024.pdf",
  },
  dorni: {
    label: "Dorni, Sharma, Saikia and Longvah (ICMR NIN), Fatty acid profile of edible oils and fats consumed in India, Food Chemistry 2018",
    url: "https://pubmed.ncbi.nlm.nih.gov/28867107/",
  },
  usda: {
    label: "USDA FoodData Central, oil, coconut (and matching entries for the other oils)",
    url: "https://fdc.nal.usda.gov/food-details/171412/nutrients",
  },
  oxidation: {
    label: "Rates of oxidation of unsaturated fatty acids, Journal of the American Oil Chemists' Society",
    url: "https://link.springer.com/article/10.1007/BF02643258",
  },
};

/* ------------------------------------------------------------------ */
/* Coconut oil in Ayurveda                                             */
/* ------------------------------------------------------------------ */

export function AyurvedaCoconutOil() {
  return (
    <GuidePage
      path="/ayurvedic-benefits-of-coconut-oil"
      seoTitle="Coconut Oil in Ayurveda: What the Texts Say"
      seoDescription="What classical Ayurvedic texts say about coconut oil (Narikela taila): rasa, guna, virya, traditional abhyanga and head oiling, and what modern science adds."
      eyebrow="Perspectives"
      title="Coconut oil in Ayurveda: what the classical texts say"
      tldr="In Ayurveda, coconut oil is Narikela taila. Classical texts, as summarised in modern Ayurvedic reviews, describe it as sweet, heavy, unctuous and cooling in effect, and as calming Vata and Pitta. It has long been used for body massage and head oiling in South India. This is heritage, not a health promise."
      updated={UPDATED}
      testid="guide-ayurveda"
      sections={[
        {
          id: "what-is-narikela",
          heading: "What are Narikela and Narikela taila?",
          body: (
            <>
              <p>
                Narikela is the Sanskrit name for the coconut palm and its fruit, and Narikela taila is the oil pressed from the kernel. Modern reviews list it among the oils discussed in the major classical texts and the later nighantus (the materia medica lexicons), including the Charaka Samhita, Sushruta Samhita, Ashtanga Hridaya, Bhavaprakasha Nighantu and Kaiyadeva Nighantu.
              </p>
              <p>
                In Kerala and coastal Karnataka, coconut oil is also the base of many traditional preparations. A 2025 paper in the CCRAS journal collected 25 internal and 7 external coconut recipes from Kerala texts such as Sahasrayogam and Chikitsamanjari. Those are medicated preparations made by trained practitioners, not plain oil, and we do not borrow their claims.
              </p>
              <p className="note">
                This page describes tradition. Karijeeva is a food, not an Ayurvedic medicine. We do not claim it treats, prevents or balances anything in your body.
              </p>
            </>
          ),
        },
        {
          id: "properties",
          heading: "What properties do classical texts describe for coconut oil?",
          body: (
            <>
              <p>
                In short: sweet, heavy, unctuous and cooling. Ayurveda describes every food and oil through a set of properties, and this is how modern reviews of the classical texts summarise Narikela taila. Read the right hand column as a translation of the tradition, not as something our oil does to you.
              </p>
              <Table
                caption="Properties of Narikela taila as described in classical texts, with plain English meaning"
                head={["Ayurvedic term", "What texts describe", "Plain English meaning"]}
                rows={[
                  ["Rasa (taste)", "Madhura", "Sweet in taste"],
                  ["Guna (qualities)", "Guru, snigdha", "Heavy and slow to digest; unctuous or oily"],
                  ["Virya (potency)", "Sheeta", "Cooling in its effect, as the tradition understands it (this is not about the oil's temperature)"],
                  ["Vipaka (after digestion)", "Madhura", "Sweet in its effect after digestion"],
                  ["Dosha", "Described as calming Vata and Pitta", "In Ayurvedic theory, a description of the oil's nature, not a promise for any person"],
                  ["Karma (actions)", "Keshya, brimhana, balya", "Traditionally described as good for hair, nourishing and strengthening"],
                ]}
              />
              <p>
                The reviews list further classical uses, several of them tied to medical conditions. We leave those out on purpose, because Karijeeva is sold as a cooking oil and describing medical uses would turn heritage into a claim we cannot make. We also do not quote verse numbers, because we have not checked them against a printed edition.
              </p>
            </>
          ),
        },
        {
          id: "daily-life",
          heading: "How has coconut oil traditionally been used in daily life?",
          body: (
            <>
              <p>
                Mostly for oiling the body and head, and for cooking along the coast. In South India, coconut oil has been the everyday oil for abhyanga (oil massage of the body) and shiro abhyanga (oiling and massaging the scalp) for generations, often on a weekend morning before a bath.
              </p>
              <ul>
                <li><strong>Abhyanga.</strong> Warm oil massaged into the body, then washed off with a warm bath. Sesame oil is the classical default in many texts; coconut oil is the common choice in coastal homes.</li>
                <li><strong>Shiro abhyanga.</strong> Oil worked into the scalp and hair, left on for a while, then washed out. Classical texts call Narikela taila keshya, a word for things considered good for the hair. Our <Link to="/coconut-oil-for-hair">hair guide</Link> covers what modern lab studies on hair fibres found.</li>
                <li><strong>Cooking.</strong> In Kerala, Tulu Nadu and the Uttara Kannada coast, coconut oil is the oil for tempering, thorans, fish curries and banana chips.</li>
                <li><strong>Gandusha.</strong> Holding oil in the mouth, the root of what is now called oil pulling. Classical texts are usually read as naming sesame oil (tila taila) for this; coconut oil is a modern favourite because of its mild taste. See our <Link to="/oil-pulling-with-coconut-oil">oil pulling guide</Link>.</li>
              </ul>
            </>
          ),
        },
        {
          id: "lab-and-ayurveda",
          heading: "Does Ayurveda's description match what a lab sees in coconut oil?",
          body: (
            <>
              <p>
                On texture, yes, in a loose way. On health effects, the two speak different languages and one cannot prove the other. What follows is our own reading, not a classical statement.
              </p>
              <p>
                Our latest nutrition report shows {sat.value} {sat.unit} of saturated fat per 100 g, and our purity report shows an iodine value of {iodine.value}, which means very few double bonds. That is why coconut oil sets solid when the weather cools and resists going rancid. An oil like this, thick and solid in the cold, fits the way the texts describe Narikela taila as guru (heavy) and snigdha (unctuous).
              </p>
              <p>
                Sheeta virya (cooling) is different. It describes the oil's effect on the body in Ayurvedic theory, and no lab test measures it. Every number on our reports is explained in plain words on the <Link to="/coconut-oil-lab-report-explained">lab report explained</Link> page.
              </p>
            </>
          ),
        },
        {
          id: "modern-science",
          heading: "What does modern science add, and where does it disagree?",
          body: (
            <>
              <p>
                Modern nutrition adds one clear caution that the classical texts do not address: coconut oil is high in saturated fat, and that raises LDL cholesterol. A 2020 analysis of 16 trials in the journal Circulation found coconut oil raised LDL cholesterol by 10.47 mg/dL compared with non tropical vegetable oils. A 2016 review in Nutrition Reviews found it raised LDL less than butter but more than unsaturated plant oils.
              </p>
              <p>
                Ayurveda is silent on LDL, because the idea did not exist. We do not use the tradition to soften that evidence. ICMR NIN advises using a combination of oils, blended or in rotation, rather than any single oil, and suggests most adults keep visible fat to about 20 to 30 g a day across all oils and ghee.
              </p>
              <p>
                Where science agrees with tradition is on practice. Coconut oil's low polyunsaturated fat makes it stable for everyday tempering, and in lab studies on hair fibres it reduced protein loss when used before washing (Rele and Mohile, 2003). Our <Link to="/coconut-oil-cooking-studies">cooking studies guide</Link> sets out what the research on heating found.
              </p>
            </>
          ),
        },
        {
          id: "how-to",
          heading: "How do you use coconut oil the traditional way today?",
          body: (
            <>
              <p>
                Warm it gently, massage it in, give it some time, then bathe. Here is the simple version most families follow. It is practical home guidance, not a treatment.
              </p>
              <Table
                caption="Warm oil body massage (abhyanga) at home, step by step"
                head={["Step", "What to do"]}
                rows={[
                  ["1. Patch test", "Rub a little oil on the inside of your forearm and wait a day. Skip it if you see redness or itching."],
                  ["2. Warm the oil", "Stand the jar or a small steel bowl of oil in hot water for a few minutes. It should feel comfortably warm on your wrist, never hot. If the oil has set solid in winter, this also melts it."],
                  ["3. Massage", "Start at the scalp, then the face, neck, arms, chest, stomach, back and legs. Long strokes on the limbs, small circles on the joints. Ten to fifteen minutes is plenty."],
                  ["4. Wait", "Let the oil sit for about 15 to 30 minutes while you relax."],
                  ["5. Bathe", "Wash with warm water and a mild soap or besan. Take care, as oiled feet and floors are slippery."],
                ]}
              />
              <h3>Head oiling (shiro abhyanga)</h3>
              <p>
                Part your hair in sections and put a few drops of warm oil on the scalp along each parting. Massage with your fingertips, not your nails, for five minutes, then comb the rest through to the ends. Leave it for an hour or overnight, and wash out with a mild shampoo.
              </p>
              <p className="note">
                Speak to a qualified vaidya or doctor for any health condition, and ask your paediatrician before starting any oil routine for a baby.
              </p>
            </>
          ),
        },
        {
          id: "quality",
          heading: "Why does the quality of the oil matter, in tradition and today?",
          body: (
            <>
              <p>
                Because every property the texts describe assumes real coconut oil, and today that cannot be taken for granted. In the old way of doing things, a family knew the palms, dried the copra and had the oil pressed nearby. Now most oil comes in a jar from people you have never met.
              </p>
              <p>
                For both cooking and oiling, we would look for three things. The oil should be pressed from dried copra without added heat, not refined, bleached or deodorised, and tested by an independent lab against the FSSAI standard for coconut oil.
              </p>
              <p>
                Karijeeva meets all three. Our coconuts grow on our family farms in Tiptur and on the Uttara Kannada coast from Gokarna to Shirali. The oil is cold pressed from dried copra with nothing added, and every batch goes to an independent food testing lab (latest: {LAB.name}, {LAB.city}, {LAB.credential}). We have tested {BRAND.batchesTested} batches so far. If you would like to try it for cooking or for your weekend oil bath, you can <Link to="/products">see sizes and prices here</Link>.
              </p>
            </>
          ),
        },
        {
          id: "glossary",
          heading: "What do the Ayurvedic words mean?",
          body: (
            <>
              <p>A short glossary of the terms on this page, in plain English.</p>
              <Table
                caption="Glossary of Ayurvedic terms used on this page"
                head={["Term", "Plain English"]}
                rows={[
                  ...AYURVEDA_HERITAGE.terms,
                  ["Narikela", "Coconut (Cocos nucifera)"],
                  ["Taila", "Oil"],
                  ["Rasa", "Taste"],
                  ["Guna", "Quality or property"],
                  ["Virya", "Potency, usually described as heating or cooling"],
                  ["Vipaka", "The taste or effect after digestion"],
                  ["Dosha", "The three body principles in Ayurveda: Vata, Pitta and Kapha"],
                  ["Brimhana", "Nourishing, building"],
                  ["Balya", "Strengthening"],
                  ["Dinacharya", "Daily routine"],
                ]}
              />
            </>
          ),
        },
      ]}
      faqs={[
        { q: "Is coconut oil cooling or heating in Ayurveda?", a: "Classical texts, as summarised in modern reviews, describe coconut oil as sheeta virya, which means cooling in its effect. This is an Ayurvedic concept about the oil's nature, not its temperature, and it is not a promise of any effect on your body." },
        { q: "Is coconut oil good for Pitta?", a: "The texts describe Narikela taila as calming Vata and Pitta, and that is why it is often mentioned for Pitta. That is a description from the tradition, not a health claim we can make for any oil. If you follow Ayurveda for a health reason, speak to a qualified vaidya." },
        { q: "Which oil does Ayurveda recommend for oil pulling?", a: "Classical texts are usually read as naming sesame oil (tila taila) for gandusha, the practice behind oil pulling. Coconut oil is a popular modern choice because of its mild taste. Oil pulling does not replace brushing, flossing or seeing a dentist." },
        { q: "Can I use cooking coconut oil for abhyanga?", a: "Yes, if it is a single ingredient, unrefined coconut oil with nothing added. Many South Indian homes have always used the same jar for cooking and for the weekend oil bath. Do a patch test first." },
        { q: "Is Karijeeva an Ayurvedic oil?", a: "No. Karijeeva is a food: cold pressed coconut oil sold under the FSSAI standard for coconut oil. It is not an Ayurvedic medicine or an Ayurveda Aahara product, and we describe the tradition as heritage only." },
        { q: "Does Ayurveda say coconut oil is heavy?", a: "Yes. The texts describe Narikela taila as guru, which means heavy and slow to digest. Modern nutrition gives a parallel caution: coconut oil is high in saturated fat, so use it in sensible amounts alongside other oils." },
        { q: "Why does coconut oil go solid, and can I still use it for massage?", a: "Coconut oil sets below about 24°C because it is mostly saturated fat. That is normal and does not affect quality. Stand the jar in warm water for a few minutes and it melts back." },
      ]}
      sources={[
        SRC.jaims,
        SRC.jdras,
        SRC.ijapr,
        SRC.circulation,
        SRC.eyres,
        SRC.ninFaq,
        SRC.ninDgi,
        SRC.hair,
      ]}
      related={[
        { label: "Your lab report, explained", to: "/coconut-oil-lab-report-explained" },
        { label: "Coconut oil for hair", to: "/coconut-oil-for-hair" },
        { label: "Oil pulling with coconut oil", to: "/oil-pulling-with-coconut-oil" },
        { label: "Cold pressed coconut oil benefits", to: "/cold-pressed-coconut-oil-benefits" },
      ]}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Coconut oil vs other cooking oils                                   */
/* ------------------------------------------------------------------ */

export function CoconutOilVsOtherOils() {
  return (
    <GuidePage
      path="/coconut-oil-vs-other-cooking-oils"
      seoTitle="Coconut Oil vs Other Cooking Oils for Indian Food"
      seoDescription="Coconut oil vs groundnut, mustard, sesame, sunflower, rice bran, olive, palm oil and ghee: fat profile, flavour, Indian uses and how ICMR NIN says to mix oils."
      eyebrow="Perspectives"
      title="Coconut oil vs other cooking oils: which is best for Indian cooking?"
      tldr="No single oil is best for everything. Coconut oil is mostly saturated fat, so it is stable for tempering and gives South Indian dishes their flavour, but it raises LDL cholesterol more than unsaturated oils. ICMR NIN advises combining oils. Use coconut oil where it shines and pair it with groundnut, mustard or sesame."
      updated={UPDATED}
      testid="guide-oils-compare"
      sections={[
        {
          id: "compare",
          heading: "How does coconut oil compare with other cooking oils?",
          body: (
            <>
              <p>
                Coconut oil is the most saturated oil in the Indian kitchen, and the least polyunsaturated. That single fact explains both its strengths and its limits. The table uses USDA FoodData Central figures in grams per 100 g of oil, so the three fat columns do not add up to exactly 100.
              </p>
              <Table
                caption="Coconut oil compared with other common Indian cooking oils and ghee"
                head={["Oil", "Saturated / MUFA / PUFA (g per 100 g)", "Flavour", "Best Indian uses", "Heat stability note", "Karijeeva view"]}
                rows={[
                  ["Coconut", "82.5 / 6.3 / 1.7", "Clear coconut aroma when unrefined", "Tempering, thoran, avial, fish curry, appam, chips, coconut chutney tadka", "Very low PUFA, so it holds up well to tempering and shallow frying", "Our oil. Use it where the coconut flavour belongs, as one oil in the kitchen"],
                  ["Groundnut", "16.9 / 46.2 / 32.0", "Mild, nutty", "Everyday sabzi, dal, poha, frying in Gujarati, Maharashtrian and Andhra homes", "Moderate PUFA; a common frying oil", "A very good partner for coconut oil"],
                  ["Mustard", "11.6 / 59.2 / 21.2", "Pungent, sharp", "Bengali fish, Punjabi sarson ka saag, pickles, North and East Indian curries", "Traditionally heated well to mellow its bite; do not let it burn", "A very good partner; ICMR NIN notes its better omega 3 profile"],
                  ["Sesame (gingelly)", "14.2 / 39.7 / 41.7", "Nutty (plain, not toasted)", "Tamil and Andhra cooking, kuzhambu, podi, pickles", "Higher PUFA; best at moderate heat", "A natural partner in South Indian kitchens"],
                  ["Sunflower", "10.3 / 19.5 / 65.7", "Neutral", "General cooking where you want no flavour", "Highest PUFA here, so it breaks down fastest with long or repeated heating", "Fine in a mix; less suited to long, hot cooking"],
                  ["Rice bran", "19.7 / 39.3 / 35.0", "Neutral", "Everyday cooking and frying", "Moderate PUFA; widely used for frying", "A good neutral partner"],
                  ["Olive", "13.8 / 73.0 / 10.5", "Fruity when unrefined", "Salads, light sautéing, fusion dishes", "Mostly MUFA, stable at moderate heat", "Good fat profile, but not a traditional Indian flavour and costly"],
                  ["Palm", "49.3 / 37.0 / 9.3", "Neutral", "Common in packaged snacks and commercial frying", "Stable, but high in saturated fat", "No reason to choose it at home over the others"],
                  ["Ghee", "61.9 / 28.7 / 3.7", "Rich, buttery", "Dal tadka, rotis, sweets, festive cooking", "Stable; high in saturated fat", "A small amount for flavour, like coconut oil"],
                ]}
              />
              <p>
                Sources: USDA FoodData Central for each oil (sunflower is the standard high linoleic type; high oleic sunflower is very different). Indian lab data agree on the headline: an ICMR NIN study of 320 oil and fat samples from across India found coconut oil had the highest saturated fat of all, at 90.84% of its fatty acids. Our own latest lab report shows {sat.value} g saturated, {mufa.value} g MUFA and {pufa.value} g PUFA per 100 g.
              </p>
            </>
          ),
        },
        {
          id: "coconut-wins",
          heading: "Where is coconut oil the better choice?",
          body: (
            <>
              <p>
                Coconut oil wins for tempering, for coastal and South Indian dishes, and for keeping well on the shelf.
              </p>
              <ul>
                <li><strong>Stability for tempering.</strong> With only about 2 g of polyunsaturated fat per 100 g, it has very few double bonds for heat and air to attack. Oxidation speeds up sharply with each extra double bond, which is why high PUFA oils suffer more with heat. That makes coconut oil a steady choice for a mustard seed and curry leaf tadka or a shallow fry at medium heat.</li>
                <li><strong>Flavour.</strong> Avial, thoran, Mangalorean fish curry and Kerala banana chips taste the way your family expects only with unrefined coconut oil. No other oil gives that.</li>
                <li><strong>A single ingredient oil.</strong> Cold pressed coconut oil can be exactly one thing: coconut, pressed and filtered. You can read what is in it on a lab report.</li>
                <li><strong>Keeps well.</strong> Low unsaturation means it resists going rancid. It turns solid in winter, which is normal.</li>
              </ul>
              <p>
                Our <Link to="/coconut-oil-cooking-temperature">cooking temperature guide</Link> explains how hot to go, and the <Link to="/cooking-with-coconut-oil">cooking guide</Link> has dish by dish tips.
              </p>
            </>
          ),
        },
        {
          id: "other-wins",
          heading: "When is another oil the better choice?",
          body: (
            <>
              <p>
                Choose an unsaturated oil for most of your daily fat, a neutral oil where coconut flavour does not belong, and a separate oil for large batch deep frying.
              </p>
              <ul>
                <li><strong>For your overall fat profile.</strong> Coconut oil raises LDL cholesterol more than unsaturated oils: a 2020 analysis of 16 trials in the journal Circulation found an average rise of 10.47 mg/dL compared with non tropical vegetable oils. Oils rich in MUFA and PUFA, such as groundnut, mustard, sesame and rice bran, should make up most of the oil you eat.</li>
                <li><strong>For neutral flavour.</strong> In rajma, chole, paneer dishes or baking where coconut would taste out of place, use groundnut, rice bran or sunflower oil.</li>
                <li><strong>For deep frying in volume.</strong> Filling a kadai for pakoras or puris takes a lot of oil. We do not suggest coconut oil for that; we have not measured its smoke point, and a cheaper neutral oil makes more sense. Whatever you fry in, never let the oil smoke and do not reuse it.</li>
              </ul>
            </>
          ),
        },
        {
          id: "icmr-nin",
          heading: "What does ICMR NIN recommend about cooking oils?",
          body: (
            <>
              <p>
                ICMR NIN, India's national nutrition institute, says to use a combination of oils, either blended or by rotation, because any single oil alone is not preferred. No single oil gives the right balance of fats on its own.
              </p>
              <ul>
                <li><strong>Amount.</strong> About 20 to 30 g of visible fat a day for most adults, counting all cooking oil, ghee and butter together. That is roughly four to six teaspoons.</li>
                <li><strong>Balance.</strong> The 2024 Dietary Guidelines for Indians aim for a diet where polyunsaturated and saturated fat are in a ratio of about 0.8 to 1, and where omega 6 is not overwhelming omega 3. Mustard oil helps on the omega 3 side.</li>
                <li><strong>Avoid.</strong> Vanaspati, and reheating oil that has already been used for frying.</li>
              </ul>
              <p>
                Read that honestly and it is good news for coconut oil, as long as it is one oil among two or three rather than the only one.
              </p>
            </>
          ),
        },
        {
          id: "two-or-three",
          heading: "How do you build a 2 or 3 oil kitchen with coconut oil in it?",
          body: (
            <>
              <p>
                Keep coconut oil for the dishes it suits, and pair it with one or two unsaturated oils for everything else. These pairings are our own suggestions based on ICMR NIN's advice to combine oils, not an official list.
              </p>
              <Table
                caption="Suggested two or three oil combinations that include coconut oil, by home cuisine"
                head={["Your kitchen", "Coconut oil for", "Pair with", "Use the partner oil for"]}
                rows={[
                  ["Karnataka (coastal and Malnad)", "Tempering, fish curry, palya, chips", "Groundnut oil, plus a little mustard or sesame", "Everyday sabzi, chitranna, frying"],
                  ["Kerala", "Most tempering, thoran, avial, appam", "Groundnut or rice bran oil, plus mustard oil sometimes", "Neutral dishes and deep frying"],
                  ["Tamil Nadu", "Avial, kootu, coconut based dishes", "Gingelly (sesame) oil and groundnut oil", "Kuzhambu, podi, pickles, daily cooking"],
                  ["North Indian homes", "South Indian dishes, an occasional coconut curry or tadka", "Mustard oil and groundnut or rice bran oil", "Sabzi, dal, parathas, frying"],
                ]}
              />
              <p>
                A simple way to rotate: keep two jars on the counter and switch the one you reach for by dish, not by week. Keep the total inside the 20 to 30 g a day guidance.
              </p>
            </>
          ),
        },
        {
          id: "cold-pressed",
          heading: "Does cold pressed vs refined matter for every oil?",
          body: (
            <>
              <p>
                Yes. Whichever oils you choose, the unrefined, cold pressed version keeps the flavour and natural compounds that refining, bleaching and deodorising remove. It also means the fat profile is what nature made it, with no blending.
              </p>
              <p>
                Refining does not change the basic fat profile much, so a refined oil and its cold pressed version sit in the same row of the table above. What changes is taste, aroma and how much you can trust what is in the jar. For coconut oil, where the flavour is the reason you are using it, unrefined makes the biggest difference. Our <Link to="/cold-pressed-coconut-oil-benefits">cold pressed benefits guide</Link> explains more.
              </p>
            </>
          ),
        },
        {
          id: "karijeeva",
          heading: "Where does Karijeeva fit in your kitchen?",
          body: (
            <>
              <p>
                As the coconut oil in your rotation. We do not ask you to throw out your groundnut or mustard oil. We ask you to make the coconut oil you already use a pure, tested, cold pressed one, and to use it for the dishes it suits best.
              </p>
              <p>
                Karijeeva is pressed from dried copra from our family farms in Tiptur and on the Uttara Kannada coast, with one ingredient and nothing refined out or added in. Every batch goes to an independent food testing lab, and our latest report met the FSSAI coconut oil standard on all six tests. Our FSSAI licence is {BRAND.fssai}. You can <Link to="/products">see sizes and prices here</Link>, and unopened jars can be returned within 7 days.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        { q: "Which oil is healthiest for Indian cooking?", a: "No single oil. ICMR NIN advises using a combination of oils, blended or in rotation, and keeping visible fat to about 20 to 30 g a day. A practical mix is one unsaturated oil such as groundnut, mustard or sesame for most cooking, plus coconut oil for the dishes it suits." },
        { q: "Is coconut oil better than sunflower oil?", a: "It depends on the job. Coconut oil is far more stable when heated because it has very little polyunsaturated fat, but it is mostly saturated fat and raises LDL cholesterol more than sunflower oil. Use coconut oil for tempering and South Indian dishes, and do not rely on it alone." },
        { q: "Coconut oil or groundnut oil for cooking?", a: "Both, for different dishes. Groundnut oil is mostly unsaturated with a mild taste, so it suits everyday sabzi, dal and frying. Coconut oil is better where you want the coconut flavour and a stable oil for tempering." },
        { q: "Can I use only coconut oil for all cooking?", a: "We would not suggest it. Coconut oil is high in saturated fat, and ICMR NIN says any single oil alone is not preferred. Many coastal families cook mostly with coconut oil; if you do, keep the amount modest and add an unsaturated oil for some of your cooking." },
        { q: "Is coconut oil good for deep frying?", a: "It is stable, but we do not recommend it for large batch deep frying. It takes a lot of oil, we have not measured our oil's smoke point, and a neutral oil is more economical. Coconut oil is best for tempering and shallow frying at medium heat." },
        { q: "Is mustard oil or coconut oil better?", a: "They do different jobs, and they pair well. Mustard oil is mostly unsaturated and brings omega 3, which ICMR NIN notes. Coconut oil brings stability and its own flavour. Using both in rotation covers more ground than either alone." },
        { q: "Is ghee healthier than coconut oil?", a: "Neither is a health food. Both are high in saturated fat: USDA lists about 62 g per 100 g for ghee and about 83 g for coconut oil. Use each in small amounts for flavour, alongside unsaturated oils." },
      ]}
      sources={[
        SRC.usda,
        SRC.dorni,
        SRC.ninFaq,
        SRC.ninDgi,
        SRC.circulation,
        SRC.eyres,
        SRC.oxidation,
      ]}
      related={[
        { label: "Cooking with coconut oil", to: "/cooking-with-coconut-oil" },
        { label: "Coconut oil cooking temperature", to: "/coconut-oil-cooking-temperature" },
        { label: "What cooking studies found", to: "/coconut-oil-cooking-studies" },
        { label: "Cold pressed coconut oil benefits", to: "/cold-pressed-coconut-oil-benefits" },
        { label: "Shop Karijeeva", to: "/products" },
      ]}
    />
  );
}
